import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Badge,
  Box,
  Container,
  Stack,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  TextField,
  InputAdornment
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RateReviewIcon from '@mui/icons-material/RateReview';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import GppBadIcon from '@mui/icons-material/GppBad';
import { useAuth } from '../context/AuthContext';
import { useIntelligenceJobs } from '../context/IntelligenceJobsContext';
import api from '../services/api';
import packageJson from '../../package.json';
import SyncStatusPopover from './SyncStatusPopover';

const APP_VERSION = packageJson.version;

// ── Changelog Data ──────────────────────────────────────────────────────────
const CHANGELOG = [
  {
    version: '1.6.7',
    date: 'July 10, 2026',
    title: 'Findings Tuning',
    entries: [
      { type: 'improved', text: 'Enabled Shared Mailbox finding excludes boardrooms/rooms and is High (not Critical); Stale Guest Access lowered to Medium' },
      { type: 'improved', text: 'Dropped Orphaned Enabled User finding (managers not used in AD); denser Findings list rows' },
    ]
  },
  {
    version: '1.6.6',
    date: 'July 9, 2026',
    title: 'Sync Ops Alerts',
    entries: [
      { type: 'feature', text: 'Operational alerts for SyncFailed, SyncStale, and SyncOverdue — durable Alert records with dashboard banner and Alerts page' },
      { type: 'feature', text: 'Successful full sync auto-resolves open Sync* alerts and runs HighRisk / LicenseWaste scans' },
      { type: 'improved', text: 'Navbar Alerts tab shows open-count badge; scheduler checks overdue vs auto-sync interval every 15 minutes' },
      { type: 'internal', text: 'Email emergency alerts deferred to a follow-up (Graph sendMail + recipient Config)' },
    ]
  },
  {
    version: '1.6.5',
    date: 'July 9, 2026',
    title: 'Sync Status Refresh Fix',
    entries: [
      { type: 'fixed', text: 'Navbar Syncing chip updates immediately after cancel — clears stale running-job cache instead of waiting for the next poll' },
      { type: 'improved', text: 'Sync status polls every 1s while running; cancelled jobs show a Sync Cancelled chip state' },
    ]
  },
  {
    version: '1.6.4',
    date: 'July 9, 2026',
    title: 'Findings Layout & Sync Popover',
    entries: [
      { type: 'improved', text: 'Identity Findings redesigned — category tabs (Security / Cost / Hygiene), severity chips, and a denser investigate list instead of a card grid' },
      { type: 'improved', text: 'Navbar Syncing chip is clickable — shows live stage progress, overall %, ETA, Cancel, and link to Sync History' },
      { type: 'improved', text: 'Dashboard no longer shows the large multi-step sync progress box; compact bar keeps overall % and current stage' },
    ]
  },
  {
    version: '1.6.3',
    date: 'July 9, 2026',
    title: 'Identity Findings',
    entries: [
      { type: 'feature', text: 'Renamed Queues → Identity Findings — security-first posture checks with Critical/High/Medium/Low severity grouping' },
      { type: 'security', text: 'New critical finding: Enabled Shared/Room/Equipment mailboxes (sign-in should be blocked per CIS / Microsoft guidance)' },
      { type: 'feature', text: 'Findings cover privileged without MFA, stale privileged, stale guests, never-signed-in + paid, stale + paid, service never signed in, orphaned users, disabled + paid, resource mailbox + paid, expired password' },
      { type: 'improved', text: 'Removed noisy Critical/High risk dump queue; deep links open the matching account filters' },
      { type: 'improved', text: 'Account list API accepts mailboxType filter for resource-mailbox queries' },
    ]
  },
  {
    version: '1.6.2',
    date: 'July 9, 2026',
    title: 'Shareable Filter & Deep Links',
    entries: [
      { type: 'feature', text: 'Shareable filter/deep links — account list filters, page, sort, open account, and row selection sync to the URL with replace (no history spam)' },
      { type: 'feature', text: 'Sync History and Sync Detail views sync toggles/filters to the URL; Settings tabs use replace navigation' },
      { type: 'fixed', text: 'Deep-link paths always win over saved localStorage view on first load' },
    ]
  },
  {
    version: '1.6.1',
    date: 'July 9, 2026',
    title: 'Dashboard Load Fix',
    entries: [
      { type: 'fixed', text: 'Dashboard no longer waits ~40s on a classifications history scan (16M+ rows from hourly syncs) — stale trend uses a live accounts snapshot instead' },
      { type: 'improved', text: 'Bootstrap endpoint cached 60s; license stats use denormalized columns; license-savings deferred until after first paint' },
      { type: 'improved', text: 'Added prune-classifications script to reclaim DB space from historical classification rows' },
    ]
  },
  {
    version: '1.6.0',
    date: 'July 8, 2026',
    title: 'Performance, Remediation & Governance',
    entries: [
      { type: 'feature', text: 'Real Graph actions: Disable, Enable, Block Sign-In, and License Remove with dry-run, audit log, and bulk selection toolbar' },
      { type: 'feature', text: 'Attention Queues page — Disabled+Paid, Stale+Paid, Never Logged In+Paid, Privileged+No MFA, Critical/High Risk, Shared Mailbox+Paid' },
      { type: 'feature', text: 'Exceptions whitelist, Service Account registry, and License SKU cost config in Settings — dashboard shows estimated $/mo waste' },
      { type: 'improved', text: 'License filters use denormalized SQL columns instead of loading all accounts into memory (fixes 30–90s Paid Licensed drill-downs)' },
      { type: 'improved', text: 'Dashboard loads via single /api/analytics/bootstrap; sync poll 2.5s; navbar shows live sync health' },
      { type: 'improved', text: 'Auth user cache + throttled lastLoginAt writes; shared cache layer with optional Redis; CSV export streams in batches' },
      { type: 'improved', text: 'Classification history only on change; usage/mailbox sync concurrency pools; optional external sync worker (SYNC_WORKER_MODE=external)' },
      { type: 'fixed', text: '/high-risk applies Critical+High filters; expired_password flag works; alerts use authenticated user; dual search UX removed from account list' },
    ]
  },
  {
    version: '1.5.3',
    date: 'April 21, 2026',
    title: 'Default Full Read Access for Authenticated Users',
    entries: [
      { type: 'feature', text: 'Any user who can sign in now gets full read access to every account by default — access to the application is gated upstream by Entra group membership, so a successful login is itself the authorization signal. Write and admin permissions remain restricted and are granted explicitly' },
      { type: 'fixed', text: 'New users were created with allowedUserTypes: [] which then collapsed to "see no accounts" via buildUserTypeFilter — they could log in successfully but the account list, dashboard, and search were silently empty until an admin manually added user types. New users are now provisioned with all four user types (Member, Guest, External, Internal)' },
      { type: 'fixed', text: 'Existing users with empty allowedUserTypes are auto-healed without a database migration — the filter helper now treats an empty array as "no restriction" instead of "block everything". Admins can still narrow access by setting an explicit subset of types' },
      { type: 'security', text: 'To lock a user out, deactivate them (isActive = false) or restrict allowedUserTypes to an explicit subset. Empty array no longer means "blocked" — it means "default full read"' },
    ]
  },
  {
    version: '1.5.2',
    date: 'April 17, 2026',
    title: 'Dashboard Hang Fix & Polling Discipline',
    entries: [
      { type: 'fixed', text: 'Production dashboard no longer hangs on an infinite spinner — Azure Static Web App was applying a default same-origin Cross-Origin-Opener-Policy that blocked MSAL\'s popup window.closed detection, causing every popup auth to hang and cascade 30+ 401s across polled requests' },
      { type: 'fixed', text: 'Single-flight 401 refresh — concurrent 401s from parallel requests now share one token-refresh promise instead of each kicking off an independent interactive login' },
      { type: 'fixed', text: 'In-flight GET dedup at the api client — identical requests fired in the same tick share a single network round-trip (defends against StrictMode double-mount and parallel chart components hitting the same endpoint)' },
      { type: 'improved', text: 'Dashboard data now cached client-side via react-query with a 60s stale time — navigating away and back to the dashboard returns instantly from cache instead of re-running all three aggregation endpoints (was ~30 seconds)' },
      { type: 'improved', text: 'Sync-status query cached (30s stale) and projected into local UI state via effect — the dashboard no longer re-fetches on every re-render' },
      { type: 'improved', text: 'Account intelligence polling is now disciplined — polls every 3 seconds only while an intelligence job is Running AND the tab is visible AND the user is authenticated; idle tabs and background tabs poll nothing' },
      { type: 'improved', text: 'Disabled refetchOnWindowFocus on the intelligence-jobs query (was overriding the global default and causing a request burst on every tab switch)' },
      { type: 'improved', text: 'Defensive Cache-Control: private, max-age=30 on /api/analytics/dashboard, /api/sync/stats, and /api/accounts/stats/classification for browser-level caching' },
      { type: 'security', text: 'Cross-Origin-Opener-Policy: unsafe-none explicitly set on the Static Web App so MSAL popup authentication can complete; this matches the Microsoft Identity Platform guidance for SPAs using popup flows' },
      { type: 'security', text: 'Cross-Origin-Embedder-Policy: unsafe-none explicitly set for the same reason' },
      { type: 'internal', text: 'Retry policy tightened on the intelligence-jobs query (retry: 0) so auth failures don\'t pile retries while the token is being refreshed' },
    ]
  },
  {
    version: '1.5.1',
    date: 'April 16, 2026',
    title: 'Auth UX Hardening & Dashboard Query Performance',
    entries: [
      { type: 'fixed', text: 'Dashboard analytics endpoint no longer takes 20–30 seconds to load — rewrote /api/analytics/dashboard from a full-table findMany (~7,800 rows including multi-KB enrichedData JSON blobs) plus in-memory JS aggregations into four parallel SQL aggregations using COUNT(*) FILTER (WHERE …); measured locally at ~17ms' },
      { type: 'fixed', text: 'Classification stats endpoint collapsed from 32 sequential count() round-trips into one FILTER-based aggregation query — measured locally at ~7ms (was ~800ms)' },
      { type: 'fixed', text: 'Sync stats endpoint no longer pulls every account row to count licenses — replaced the full-table findMany with a single CTE-backed FILTER query, measured locally at ~9ms (was 2–5 seconds)' },
      { type: 'fixed', text: '10–15s hang on token expiry — silent token acquisition now races a 6s timeout and automatically falls back to an interactive popup on any silent failure instead of returning null and leaving the UI in limbo' },
      { type: 'fixed', text: 'Double-login race removed — profile-picture fetch no longer calls acquireTokenPopup on silent failure; it now falls through silently to initials, eliminating the second popup that was racing the main auth popup' },
      { type: 'improved', text: 'MSAL cache moved to localStorage so a new tab inherits the active session — previously sessionStorage meant every new tab was logged out' },
      { type: 'improved', text: 'In-flight promise dedup for both silent and popup token acquisition — concurrent callers share a single refresh attempt instead of each kicking off their own' },
      { type: 'improved', text: 'Global axios 401 interceptor now force-refreshes the token once and retries the original request transparently, so a single token expiry is invisible to the user' },
      { type: 'improved', text: 'authAxios memoized — a single axios instance with interceptors attached once, instead of creating a new instance on every call' },
      { type: 'improved', text: 'MSAL iframeHashTimeout and loadFrameTimeout tightened to 6s so silent refresh can\'t hang longer than that before the popup fallback kicks in' },
      { type: 'security', text: 'MSAL claimsBasedCachingEnabled turned on — reduces spurious refreshes when the same claims set is requested' },
      { type: 'internal', text: 'Shared helpers (acquireSilent, acquirePopup) added to AuthContext with useRef-backed promise caches; getIdToken now accepts { forceRefresh } to bypass the silent cache on demand' },
    ]
  },
  {
    version: '1.5.0-preview',
    date: 'March 27, 2026',
    title: 'Defender for Identity Integration — On-Prem DC Sign-In Visibility',
    entries: [
      { type: 'feature', text: 'Microsoft Defender for Identity integration — on-premises Domain Controller authentication events (Kerberos, NTLM, LDAP) are now captured via the M365 Advanced Hunting API and surfaced throughout the application' },
      { type: 'feature', text: 'Bulk on-prem logon sync — a single KQL query fetches all DC logon summaries during full sync (step 4/6), running in parallel with security context, mailbox, and usage intelligence steps at zero additional sync time' },
      { type: 'feature', text: 'Per-account on-prem logons — Collect Intel and single-account sync now fetch the last 25 DC authentication events for individual accounts alongside Exchange, usage, and access intelligence' },
      { type: 'feature', text: 'On-Prem DC section in Account Detail — Sign-in Activity panel now shows cloud (Entra ID) and on-prem (Defender) sign-in data side by side, with last DC auth time, 30-day logon count, auth types, and device/DC names' },
      { type: 'feature', text: 'On-Prem DC Logons table in Intelligence tab — recent DC authentication events displayed with date, action type (success/failed chips), logon type, device, IP, and protocol columns' },
      { type: 'feature', text: '"DC" / "DC only" chips in account list — Last Sign-In column now shows a compact badge when an account has on-prem DC activity, with tooltip for full details' },
      { type: 'feature', text: '"On-Prem DC Active" filter — new option in the Last Sign-In advanced filter to find accounts with Defender for Identity logon data' },
      { type: 'feature', text: 'Defender API endpoints — GET /api/defender/test (connectivity check), GET /api/defender/logons (preview), GET /api/defender/logons/:upn (per-account), POST /api/defender/sync (standalone bulk sync)' },
      { type: 'improved', text: 'Classification now considers both cloud and on-prem sign-ins — accounts with DC activity are no longer marked Inactive/Never Logged In even if they have no Entra cloud sign-in' },
      { type: 'improved', text: 'Stale detection uses effective last sign-in — the most recent date from either Entra ID or Defender is used for the 90-day staleness check' },
      { type: 'improved', text: 'Risk scoring reduced for on-prem active accounts — "never logged in" penalties (+20 base, +15 stale member, +15 license waste, +20 privileged unused) no longer apply to accounts with DC authentication activity' },
      { type: 'improved', text: 'Dashboard analytics updated — MFA adoption, license utilization, and activity distribution charts all use effective last sign-in from both sources' },
      { type: 'improved', text: 'Report exports updated — Last Sign-In column in CSV/XLSX/PDF exports shows on-prem dates with "(DC)" suffix when cloud sign-in is absent' },
      { type: 'improved', text: 'Account Overview "Last Login" now shows on-prem DC date with label when cloud sign-in is absent, instead of "Never"' },
      { type: 'improved', text: 'Flags column: 🏢 icon for accounts active on-prem DC only (no cloud sign-in), replacing the 🚫 never-logged-in icon for those accounts' },
      { type: 'improved', text: 'Enrichment service now merges with existing enrichedData — on-prem sign-in data from Defender survives enrichment refreshes instead of being overwritten' },
      { type: 'fixed', text: 'classifyAllAccounts now writes hasNeverLoggedIn back to the database — previously only isStale and isOrphan were persisted, causing Entra sync to overwrite the corrected value' },
      { type: 'fixed', text: 'Console log noise reduced — /api/accounts/intelligence/jobs and /api/auth/status polling endpoints excluded from request logging' },
      { type: 'security', text: 'AdvancedHunting.Read.All permission added to app registration — enables read-only access to Defender for Identity DC authentication telemetry via the M365 Defender API' },
      { type: 'security', text: 'Defender API queries are read-only and use the same Azure AD app credentials as Graph API — no additional secrets or service accounts required' },
      { type: 'internal', text: 'Database / deployment note: no Prisma schema changes. On-prem sign-in data is stored in the existing enrichedData JSON column under the onPremSignIn key. The AdvancedHunting.Read.All permission must be granted on the "Microsoft Threat Protection" API in the app registration before deployment — run: az ad app permission add --id <APP_ID> --api 8ee8fdad-f234-4243-8f3b-15c294843740 --api-permissions 7734e8e5-8dde-42fc-b5ae-6eafea078693=Role && az ad app permission admin-consent --id <APP_ID>' },
    ]
  },
  {
    version: '1.4.0',
    date: 'March 9, 2026',
    title: 'Account Notes, Filter Overhaul & Performance Tuning',
    entries: [
      { type: 'feature', text: 'Account Notes — rich-text notes with author attribution on any account. Supports bold, italic, lists, blockquotes, links, and code via TipTap editor' },
      { type: 'feature', text: 'Note attachments — paste images directly into the editor, or attach files (PDF, XLSX, images, etc.) with inline thumbnail preview, download, and full-screen preview' },
      { type: 'feature', text: 'Notes filter — filter accounts by "Has Notes" or "No Notes" in the account list, with a Notes count column in the data grid' },
      { type: 'feature', text: 'Notes API — full CRUD for account notes with file upload/download, supporting local disk or Azure Blob storage backends' },
      { type: 'feature', text: 'User Source filter — replaces "User Type" with simplified "Entra ID (Internal)" and "Guests (External)" options' },
      { type: 'feature', text: 'Two-column filter grids — Account Type (11 items), Risk Level, Activity, Account Flags, Last Sign-In, and Devices now open as compact grid menus instead of tall scrollable lists' },
      { type: 'improved', text: 'Collect Intel redesigned — moved inline next to account name as a compact icon+label button with subtle wiggle animation to attract attention without being intrusive' },
      { type: 'improved', text: 'Filter panel toggling is now instant — replaced MUI Collapse animation with CSS display toggle, eliminating layout recalculation lag' },
      { type: 'improved', text: 'All dropdown menu transitions removed — filter menus and popovers open/close with zero animation delay globally' },
      { type: 'improved', text: 'MUI ripple effect disabled globally — removes the expanding circle click animation from all buttons, chips, tabs, checkboxes, and menu items for snappier interactions' },
      { type: 'improved', text: 'Input border transition removed — filter select controls no longer animate border color changes on hover/focus' },
      { type: 'improved', text: 'Hide Filters relocated — moved from the filter controls row into the active filter chips row as a compact blue chip, saving vertical space' },
      { type: 'improved', text: 'Clear All filter chip restyled — now a solid red filled chip for high visibility' },
      { type: 'improved', text: 'Delete confirmation dialogs — native window.confirm replaced with styled MUI Dialog for note deletion' },
      { type: 'improved', text: 'Account Type filter reordered — now sits directly next to User Source for logical grouping' },
      { type: 'security', text: 'Rich-text sanitization — all note HTML content is sanitized server-side via sanitize-html before storage, stripping script tags, event handlers, and dangerous attributes to prevent stored XSS' },
      { type: 'security', text: 'Client-side sanitization — note content rendered with DOMPurify to double-guard against XSS even if server-side sanitization is bypassed' },
      { type: 'security', text: 'File upload validation — attachments validated for MIME type, file size limits, and storage key uniqueness; uploaded by user ID tracked for audit trail' },
      { type: 'security', text: 'Dev-mode auth fix — mock dev user now uses a valid UUID instead of a plain string, preventing foreign key violations when creating notes or attachments in development' },
      { type: 'internal', text: 'Database / deployment note: this release adds 2 new tables — `account_notes` and `account_note_attachments` — plus relations to `accounts` and `atlas_users`. Run `npx prisma db push` or `npx prisma migrate deploy` before deployment. No existing data is modified; the migration is additive only. If using Azure Blob for attachment storage, set AZURE_STORAGE_CONNECTION_STRING and AZURE_STORAGE_CONTAINER_NAME environment variables' },
    ]
  },
  {
    version: '1.3.0',
    date: 'March 9, 2026',
    title: 'Usage Intelligence Sync, Intel Jobs & Review Queue Removal',
    entries: [
      { type: 'feature', text: 'Tenant-wide Usage Intelligence sync — M365 service usage, Copilot, email, Teams, and OneDrive activity reports are now fetched in bulk during full sync and stored per-account, so every account has usage data without running Collect Intel individually' },
      { type: 'feature', text: 'Usage Intelligence Refresh — new standalone refresh action updates only tenant-wide usage data without running a full sync, with progress and completion reflected in Sync History and Settings' },
      { type: 'feature', text: 'Account Intelligence Job System — background intelligence collection now supports real-time SSE progress tracking plus a new Intel Jobs panel in the header for active and recent collections' },
      { type: 'feature', text: 'License Utilization dashboard redesign — the card now spans the full row with an expanded breakdown, waste callout, and direct drilldowns into actionable licensed-account segments' },
      { type: 'improved', text: 'Full sync pipeline now runs mailbox type detection, security context sync, and usage intelligence collection in parallel before classification, reducing total runtime for major refreshes' },
      { type: 'improved', text: 'Account Intelligence now blends bulk-synced usage data with on-demand deep intel, clearly labels the source of each dataset, and skips redundant usage fetches when fresh bulk data already exists' },
      { type: 'improved', text: 'Dashboard sync status and scheduler health now ignore non-dashboard job noise by filtering to relevant sync job types and cleaning stale running jobs consistently across endpoints' },
      { type: 'fixed', text: 'Bulk usage intelligence updates are now excluded from sync change detection so recurring report churn no longer pollutes audit diffs or makes quiet syncs look noisy' },
      { type: 'fixed', text: 'Risk scoring no longer relies on manual review history caps — classification now reflects the underlying account data directly instead of suppressing scores after a previous Keep decision' },
      { type: 'security', text: 'Review Queue removed for this release — the review pages, routes, middleware, navigation, user-management toggle, and `canAccessReviewQueue` permission checks have all been deleted from the application surface' },
      { type: 'security', text: 'Collect Intel and usage reporting now reduce unnecessary Microsoft 365 calls by reusing fresh tenant-wide usage snapshots, lowering repeated access to report endpoints during ad-hoc investigations' },
      { type: 'internal', text: 'Database / deployment note: this release adds 2 nullable columns to `accounts` (`usageIntelligence`, `usageFetchedAt`), drops the `reviews` table, and removes `canAccessReviewQueue` from `atlas_users`. Run `prisma db push` before or alongside deployment. The table drop intentionally deletes legacy review records; no separate data migration script is required' },
    ]
  },
  {
    version: '1.2.5 preview',
    date: 'March 8, 2026',
    title: 'Performance Overhaul, Sync History Redesign & Build Tooling',
    entries: [
      { type: 'feature', text: 'TanStack React Query data layer — accounts, filter options, license catalog, and account detail panels now use cached queries with stale-while-revalidate, eliminating unnecessary loading spinners and enabling instant re-opens' },
      { type: 'feature', text: 'Hover-prefetch on account rows — hovering over a table row starts loading that account\'s detail, timeline, and context data in the background so the panel opens instantly on click' },
      { type: 'feature', text: 'Sync History chart aggregation — the Changes Over Time chart now aggregates by day instead of showing one bar per job, and excludes single-account syncs so the trend line reflects real recurring sync activity' },
      { type: 'feature', text: 'Consecutive no-change sync compression — repeated scheduled syncs with zero tracked changes are collapsed into summary rows in the audit table, keeping the log readable when recurring syncs run frequently' },
      { type: 'feature', text: 'Route-level code splitting — all pages lazy-load behind React Suspense boundaries; the Account Detail drawer is also lazy-loaded on first open, reducing initial bundle by ~60%' },
      { type: 'feature', text: 'Vendor chunk splitting — DataGrid, Recharts, MSAL, MUI, React, and router are split into separate cached chunks for faster incremental loads' },
      { type: 'improved', text: 'Account list no longer flashes/dims when closing the detail panel — data is only re-fetched if an enrich or sync action was performed; table keeps previous rows visible via placeholderData during background refreshes' },
      { type: 'improved', text: 'Account table sort stability — backend orderBy now includes a deterministic id tie-breaker, so accounts with the same risk score no longer shuffle position between requests' },
      { type: 'improved', text: 'Filter options endpoint optimized — replaced full-table scan with Prisma DISTINCT queries, reducing response time for department/company/office dropdowns' },
      { type: 'improved', text: 'Context endpoint optimized — percentile calculation now uses COUNT/aggregate queries instead of loading all risk scores into memory' },
      { type: 'improved', text: 'Backend response caching — filter-options (5 min TTL) and account context (2 min TTL) are now cached in-memory on the server' },
      { type: 'improved', text: 'Auto-enrich is now non-blocking — the account detail panel renders immediately with existing data while enrichment runs in the background' },
      { type: 'improved', text: 'DataGrid columns memoized — column definitions and renderCell functions no longer recreate on every render, reducing unnecessary DataGrid re-renders' },
      { type: 'improved', text: 'Sync History single-account toggle no longer affects the chart — the toggle still controls the audit table, but the chart always reflects recurring/manual sync trends only' },
      { type: 'fixed', text: 'Account detail panel close no longer triggers unnecessary data refresh — a dataChanged flag tracks whether enrich/sync actually ran, skipping the re-fetch on view-only closes' },
      { type: 'fixed', text: 'Bulk action handler referenced undefined setRowSelection — corrected to setRowSelectionModel' },
      { type: 'fixed', text: 'AccountTimeline case-block lint error — added block scope to case clause with lexical declarations' },
      { type: 'fixed', text: 'ReviewQueueMUI unused eslint-disable directive removed' },
      { type: 'security', text: 'Frontend ESLint configuration added — new .eslintrc.json with react/react-hooks plugins for consistent code quality enforcement' },
      { type: 'security', text: 'Backend ESLint linebreak-style rule relaxed to off — prevents cross-platform CI failures without reducing code quality coverage' },
      { type: 'security', text: '.editorconfig and .gitattributes added — enforces consistent LF line endings and UTF-8 encoding across all contributors and CI environments' },
      { type: 'internal', text: 'Database / deployment note: this release adds 5 new indexes to the accounts table (riskScore, enabled, department, entraUserType, and a composite on enabled+removedFromSource+riskScore). These are index-only changes — no column additions or data migrations. Run `prisma db push` or apply the indexes manually before deploying. No other schema changes are required' },
    ]
  },
  {
    version: '1.2.2',
    date: 'March 6, 2026',
    title: 'New Hire Lifecycle Controls & Sync Reliability',
    entries: [
      { type: 'feature', text: 'New Hire lifecycle bucket — enabled member accounts created within the last 30 days and never used are now classified as `New Hire` instead of being folded into generic inactive/dormant user handling' },
      { type: 'feature', text: 'New Hire visibility across the app — Account List, Review Queue, Dashboard, and Account Detail now show dedicated labels, filters, drilldowns, and grace-window context for pre-start employee accounts' },
      { type: 'improved', text: 'Grace-window risk scoring tuned for pre-start users — typical New Hire accounts now stay in the low-risk 20-25 range during the first 30 days by suppressing never-used, device, and blast-radius penalties and softening the temporary no-MFA penalty' },
      { type: 'improved', text: 'Dashboard analytics and reports now track New Hire accounts separately from dormant / never-used users, including MFA adoption, paid-license utilization, executive reporting, compliance exports, and legacy classification compatibility' },
      { type: 'fixed', text: 'Classification history and compatibility views now preserve `New Hire` instead of collapsing it back into generic `Inactive`, keeping review history, timelines, and exports consistent with current account state' },
      { type: 'fixed', text: 'Classification-only and security-only refresh actions no longer get blocked by stale `sync_jobs` records — stale running jobs are now auto-cleaned across sync status and refresh endpoints' },
      { type: 'security', text: 'Review queues and critical-account workflows no longer escalate pre-start employee accounts solely because they have not yet signed in or enrolled MFA during the 30-day onboarding grace period; privileged and post-grace rules still apply' },
      { type: 'internal', text: 'Database / deployment note: no Prisma schema changes or production migration steps are required for v1.2.2. The one-time stale sync-job cleanup performed in development is now handled automatically by the application and should not need manual DB intervention in production' },
    ]
  },
  {
    version: '1.2.1',
    date: 'February 16, 2026',
    title: 'Account Intelligence Suite & Sync Improvements',
    entries: [
      { type: 'feature', text: 'Account Intelligence Suite — new "Intelligence" tab in account detail collects Exchange mailbox data, M365 service usage, Copilot activity, recent sign-ins, and app consents on demand' },
      { type: 'feature', text: 'Exchange mailbox forwarding detection — detects admin-level mail forwarding (ForwardingSmtpAddress) via Exchange Online Admin API, separate from inbox rule forwarding' },
      { type: 'feature', text: 'Exchange inbox rules analysis — detects forwarding/redirect rules, auto-reply status, mail folder statistics, and last email sent/received dates' },
      { type: 'feature', text: 'M365 service usage per user — shows last activity date for Exchange, Teams, SharePoint, OneDrive, and Yammer with license status and color-coded freshness' },
      { type: 'feature', text: 'Copilot usage tracking — shows per-app Copilot activity (Teams, Word, Excel, PowerPoint, Outlook) to identify unused Copilot licenses' },
      { type: 'feature', text: 'Recent sign-in details — last 10 sign-ins with client app, IP address, location, and success/failure status' },
      { type: 'feature', text: 'App consents and role assignments — shows third-party OAuth consents and enterprise app assignments per user' },
      { type: 'feature', text: 'Full-screen toggle for account detail sidebar — expand to full-screen overlay for better visibility of intelligence data' },
      { type: 'feature', text: 'Real-time sync progress — single-account sync now shows live step-by-step progress via Server-Sent Events (identity, mailbox, security, classification, intelligence)' },
      { type: 'feature', text: '"Collect Intel" button in tab bar — always visible, automatically switches to Intelligence tab when complete' },
      { type: 'improved', text: 'M365 usage reports now correctly parsed — fixed empty response from Graph SDK by using arraybuffer response type for CSV report endpoints' },
      { type: 'improved', text: 'Sync notification simplified — short confirmation message instead of dumping all technical details; auto-dismisses after 4 seconds' },
      { type: 'improved', text: 'Usage reports cached at tenant level (1-hour TTL) — avoids redundant bulk API calls when inspecting multiple accounts' },
      { type: 'fixed', text: 'Classification no longer overwritten during sync — activityStatus, riskScore, and riskLevel are preserved between syncs instead of being reset to defaults' },
      { type: 'fixed', text: 'False-positive license change detection — license arrays now compared by SKU ID set instead of JSON.stringify, eliminating phantom changes from JSONB key reordering' },
      { type: 'fixed', text: 'Manager foreign key violation on single-account sync — manager ID now looked up by objectId in the local database instead of using raw Entra object ID' },
      { type: 'fixed', text: 'License change tracking added to sync — entraLicenses now included in TRACKED_FIELDS so license assignment changes are logged in sync history' },
    ]
  },
  {
    version: '1.2.0',
    date: 'February 15, 2026',
    title: 'Smart Classification, Interactive Dashboard & Filter Overhaul',
    entries: [
      { type: 'feature', text: 'Smart account classification engine — 11 account types (User, New Hire, Guest, ServiceAccount, SharedMailbox, Boardroom, SystemAccount, DeviceAccount, TestAccount, FunctionalAccount, ApplicationAccount) detected via API data, licensing, and pattern analysis with false-positive safeguards' },
      { type: 'feature', text: 'Activity status split — new activityStatus field (Active, Inactive, Stale) separated from account type, replacing the overloaded classificationStatus' },
      { type: 'feature', text: 'Interactive dashboard — all charts, donut segments, legend chips, and summary pills are clickable, navigating directly to the filtered account list' },
      { type: 'feature', text: '"How it works" dialogs for Account Type Breakdown and Activity Status Breakdown explaining detection logic, confidence levels, and risk impact' },
      { type: 'feature', text: 'Specific license filter — searchable multi-select Autocomplete to filter accounts by exact license SKU name' },
      { type: 'feature', text: 'Free vs paid license detection — license catalog flags free/trial/viral licenses; new "Paid Licenses" and "5+ Licenses" filter options' },
      { type: 'feature', text: 'Singleton Prisma client to prevent database connection pool exhaustion during concurrent operations' },
      { type: 'feature', text: 'Dedicated analytics API endpoint for optimized dashboard data aggregation' },
      { type: 'feature', text: 'GET-based export route — export uses identical query parameters as the account list, guaranteeing filter parity' },
      { type: 'feature', text: 'Changelog type filter chips — clickable New/Improved/Fixed/Security toggles to filter changelog entries' },
      { type: 'feature', text: '"External Member" visual indicator (amber chip) for accounts with #EXT# in UPN but Member user type' },
      { type: 'improved', text: 'Filter bar redesigned — centered layout, compact controls, "More" button with TuneIcon for advanced filters, auto-opens when advanced filters are active' },
      { type: 'improved', text: 'License Utilization chart now shows paid-license-only metrics with accurate click-through navigation' },
      { type: 'improved', text: 'Login Activity chart click-throughs use range-based filters (e.g., 181-365 days) matching exact bucket counts instead of cumulative values' },
      { type: 'improved', text: 'Stale classification corrected — accounts that never logged in are now Inactive, not Stale; Stale strictly means "logged in before but 90+ days ago"' },
      { type: 'improved', text: 'Account Flags filter removed (redundant with Activity Status); Company filter removed from advanced panel to streamline UI' },
      { type: 'improved', text: 'Hide Filters and Columns buttons moved inline; less vertical space wasted' },
      { type: 'improved', text: 'Risk scoring refined for all 10 account types with type-specific factors and caps' },
      { type: 'improved', text: 'Prisma upgraded from 5.7.0 to 5.22.0' },
      { type: 'fixed', text: 'Export now honors all active filters — previously exported all accounts regardless of filter state' },
      { type: 'fixed', text: 'Search underscore/percent bug — _ and % characters are now escaped in database queries (PostgreSQL ILIKE wildcards)' },
      { type: 'fixed', text: 'UUID deserialization error in authorize middleware — no longer returns invalid string as UUID filter' },
      { type: 'fixed', text: '365+ URL encoding — + character now properly encoded as %2B in dashboard navigation links' },
      { type: 'fixed', text: 'Dashboard Licensed count aligned with Paid Licenses filter (was including free licenses in count)' },
      { type: 'fixed', text: 'Top Risk Departments chart text overlap resolved with wider labels and ellipsis truncation' },
      { type: 'fixed', text: 'Privileged Access filter placeholder no longer shows "Privileged" as the selected value when empty' },
    ]
  },
  {
    version: '1.1.5',
    date: 'February 14, 2026',
    title: 'Mailbox Type Detection & Unified Sync',
    entries: [
      { type: 'feature', text: 'Shared mailbox detection — shared mailboxes are now identified via Microsoft Graph API (mailbox usage report + per-user mailboxSettings) instead of unreliable name-pattern guessing' },
      { type: 'feature', text: 'Room/boardroom detection — room mailboxes detected via Graph Places API (/places/microsoft.graph.room) for 100% accurate identification' },
      { type: 'feature', text: 'New "Sync Mailbox Types" step in the full sync pipeline — fetches room list and mailbox usage report in bulk, runs between Entra sync and security context' },
      { type: 'feature', text: 'Unified single-account sync — the Sync button now performs identity sync, mailbox type detection, security enrichment, and re-classification in one click' },
      { type: 'feature', text: 'New database fields: mailboxType (user/shared/room/equipment) and roomCapacity for room metadata' },
      { type: 'feature', text: 'Searchable changelog — filter release notes by keyword' },
      { type: 'improved', text: 'Room detection now shows how it was identified: confirmed via API, confirmed via license, or best-effort pattern match' },
      { type: 'improved', text: 'Classification confidence scores: 0.95 for API/license-confirmed accounts, 0.5 for pattern-guessed rooms' },
      { type: 'improved', text: 'Risk scoring overhauled for shared mailboxes — MFA, device, and staleness checks skipped (no interactive sign-in); key risks are privileged access and license waste' },
      { type: 'improved', text: 'Risk scoring overhauled for rooms — capped at 15 (unlicensed) or 25 (AV-equipped); only privileged access and license waste assessed' },
      { type: 'improved', text: 'Dashboard risk scoring explanation updated with new room and shared mailbox scoring rules' },
      { type: 'improved', text: 'SharedMailbox added to Dashboard classification breakdown and Review Queue filters' },
      { type: 'fixed', text: 'Shared mailboxes and rooms are now classified correctly even when disabled in Entra ID — classification priority reordered so mailbox/room type is checked before disabled status' },
      { type: 'fixed', text: 'Mailbox usage report now uses beta Graph API endpoint which includes the Recipient Type column (v1.0 does not)' },
      { type: 'fixed', text: 'Report ReadableStream response properly consumed — was previously returning empty data' },
      { type: 'fixed', text: 'Removed false-positive pattern matching for shared mailboxes (info@, hr@, finance@ etc.) — detection is now purely API-driven' },
    ]
  },
  {
    version: '1.1.0',
    date: 'February 6, 2026',
    title: 'PIM Role Detection & Changelog',
    entries: [
      { type: 'feature', text: 'Privileged Identity Management (PIM) role detection — active and eligible Entra ID directory roles are now detected via Microsoft Graph PIM APIs' },
      { type: 'feature', text: 'New database fields: directoryRoles, directoryRoleCount, pimEligibleRoles, pimEligibleRoleCount, isPrivileged' },
      { type: 'feature', text: 'Bulk PIM sync — active and eligible role assignments fetched tenant-wide during security context refresh' },
      { type: 'feature', text: 'Per-account PIM enrichment — individual account refresh now fetches PIM roles in parallel with existing data' },
      { type: 'feature', text: 'Account Detail Modal now shows Active Directory Roles, PIM Eligible Roles, and Admin Groups as separate sections' },
      { type: 'feature', text: 'CSV/XLSX exports now include "Active Roles" and "PIM Eligible Roles" columns' },
      { type: 'feature', text: 'Version changelog popup — click the version number in the header to view release history' },
      { type: 'improved', text: 'Risk scoring updated: privileged detection now combines directory roles + PIM-eligible roles + admin groups' },
      { type: 'improved', text: 'New risk factor: PIM-eligible accounts without active roles score +10 (can self-elevate on demand)' },
      { type: 'improved', text: 'Classification factors now show detailed breakdown of role sources (active roles, PIM-eligible, admin groups)' },
      { type: 'improved', text: 'Privileged access filters now use isPrivileged boolean for accurate results across all views' },
      { type: 'fixed', text: 'Privileged detection no longer relies solely on group name pattern matching — actual Entra directory roles are now queried' },
      { type: 'fixed', text: 'False positives eliminated — groups with admin-like names but no actual admin rights are no longer flagged as privileged' },
    ]
  },
  {
    version: '1.0.9',
    date: 'January 28, 2026',
    title: 'Security Context & Bulk MFA',
    entries: [
      { type: 'feature', text: 'Bulk MFA status sync using Microsoft Graph reporting endpoint' },
      { type: 'feature', text: 'Security context sync for MFA, devices, and privileged group detection' },
      { type: 'improved', text: 'Sync progress tracking with real-time UI updates' },
      { type: 'fixed', text: 'MFA detection accuracy improved with bulk reporting data' },
    ]
  },
  {
    version: '1.0.8',
    date: 'January 20, 2026',
    title: 'User Management & Review Workflow',
    entries: [
      { type: 'feature', text: 'Manual account review workflow with role-based access control' },
      { type: 'feature', text: 'User management panel for administrators' },
      { type: 'feature', text: 'Batch review submission support for account disposition decisions' },
      { type: 'improved', text: 'Account list filters expanded with department, company, and device count' },
    ]
  },
];

const ENTRY_CONFIG = {
  feature:  { label: 'New',      color: '#16a34a', bg: '#f0fdf4', icon: <AutoAwesomeIcon sx={{ fontSize: 16 }} /> },
  improved: { label: 'Improved', color: '#2563eb', bg: '#eff6ff', icon: <SpeedIcon sx={{ fontSize: 16 }} /> },
  fixed:    { label: 'Fixed',    color: '#dc2626', bg: '#fef2f2', icon: <BugReportIcon sx={{ fontSize: 16 }} /> },
  security: { label: 'Security', color: '#7c3aed', bg: '#f5f3ff', icon: <SecurityIcon sx={{ fontSize: 16 }} /> },
  internal: { label: 'Internal', color: '#6b7280', bg: '#f9fafb', icon: <BuildIcon sx={{ fontSize: 16 }} /> },
};

const CHANGELOG_TYPE_FILTERS = [
  { key: 'feature', label: 'New', config: ENTRY_CONFIG.feature },
  { key: 'improved', label: 'Improved', config: ENTRY_CONFIG.improved },
  { key: 'fixed', label: 'Fixed', config: ENTRY_CONFIG.fixed },
  { key: 'security', label: 'Security', config: ENTRY_CONFIG.security },
];

function ChangelogDialog({ open, onClose }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);

  const filteredChangelog = useMemo(() => {
    const q = search.trim().toLowerCase();
    const hasTypeFilter = typeFilter.length > 0;

    return CHANGELOG.map(release => {
      let entries = release.entries;

      // Filter by type
      if (hasTypeFilter) {
        entries = entries.filter(e => typeFilter.includes(e.type));
      }

      // Filter by search
      if (q) {
        const matchingEntries = entries.filter(e => e.text.toLowerCase().includes(q));
        const titleMatch = release.title.toLowerCase().includes(q) || release.version.includes(q);
        if (titleMatch && matchingEntries.length === 0) entries = release.entries.filter(e => !hasTypeFilter || typeFilter.includes(e.type));
        else if (matchingEntries.length > 0) entries = matchingEntries;
        else if (!titleMatch) return null;
      }

      if (entries.length === 0) return null;
      return { ...release, entries };
    }).filter(Boolean);
  }, [search, typeFilter]);

  const toggleTypeFilter = (key) => {
    setTypeFilter(prev =>
      prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
    );
  };

  // Reset when dialog closes
  const handleClose = () => { setSearch(''); setTypeFilter([]); onClose(); };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
        color: 'white',
        py: 2,
        px: 3
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <NewReleasesIcon />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              What's New in ATLAS
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Release History & Changelog
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box sx={{ px: 2, pt: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search changelog..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2, bgcolor: 'white', fontSize: '0.85rem' }
          }}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {CHANGELOG_TYPE_FILTERS.map(({ key, label, config }) => {
            const selected = typeFilter.includes(key);
            return (
              <Chip
                key={key}
                label={label}
                size="small"
                onClick={() => toggleTypeFilter(key)}
                icon={React.cloneElement(config.icon, { sx: { fontSize: 14 } })}
                sx={{
                  height: 26,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  ...(selected
                    ? { bgcolor: config.bg, color: config.color, border: `1px solid ${config.color}40` }
                    : { bgcolor: 'white', color: 'text.secondary', border: '1px solid', borderColor: 'divider' }),
                  '& .MuiChip-icon': { color: 'inherit', ml: '6px', mr: '-2px' }
                }}
                variant={selected ? 'filled' : 'outlined'}
              />
            );
          })}
        </Stack>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        {filteredChangelog.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {search.trim() || typeFilter.length > 0
                ? `No results${search.trim() ? ` matching "${search}"` : ''}${typeFilter.length > 0 ? ` for selected filters` : ''}`
                : 'No changelog entries'}
            </Typography>
          </Box>
        )}
        {filteredChangelog.map((release, releaseIdx) => (
          <Box key={release.version} sx={{ borderBottom: releaseIdx < filteredChangelog.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
            {/* Release Header */}
            <Box sx={{
              px: 3,
              py: 2,
              bgcolor: releaseIdx === 0 ? 'rgba(37, 99, 235, 0.04)' : 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip
                  label={`v${release.version}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    bgcolor: releaseIdx === 0 ? '#1d4ed8' : '#6b7280',
                    color: 'white',
                    height: 26
                  }}
                />
                {releaseIdx === 0 && (
                  <Chip label="Latest" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem', color: '#16a34a', borderColor: '#16a34a' }} />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {release.title}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', ml: 1 }}>
                {release.date}
              </Typography>
            </Box>

            {/* Entries */}
            <Box sx={{ px: 3, py: 1.5 }}>
              {release.entries.map((entry, entryIdx) => {
                const config = ENTRY_CONFIG[entry.type] || ENTRY_CONFIG.internal;
                return (
                  <Box
                    key={entryIdx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 0.75,
                      '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'grey.100' }
                    }}
                  >
                    <Chip
                      icon={config.icon}
                      label={config.label}
                      size="small"
                      sx={{
                        mt: 0.25,
                        height: 22,
                        minWidth: 90,
                        bgcolor: config.bg,
                        color: config.color,
                        fontWeight: 600,
                        fontSize: '0.68rem',
                        border: `1px solid ${config.color}20`,
                        '& .MuiChip-icon': { color: config.color, ml: '6px', mr: '-2px' },
                        '& .MuiChip-label': { px: 0.75 },
                        flexShrink: 0
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5, py: 0.25 }}>
                      {entry.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function NavbarMUI({ currentView, onNavigate, collapsed = false, onToggleCollapse }) {
  const { user, profilePicture, logout, isAdmin, isReviewer, isViewer, authEnabled } = useAuth();
  const { jobs: intelligenceJobs, activeJobCount, panelOpen, setPanelOpen } = useIntelligenceJobs();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const alertsSummaryQuery = useQuery({
    queryKey: ['alerts-summary'],
    queryFn: async () => {
      const res = await api.get('/api/alerts/summary');
      return res.data || null;
    },
    staleTime: 30_000,
    refetchInterval: 60_000
  });
  const openAlertCount = alertsSummaryQuery.data?.openCount || 0;

  // Build tabs array based on permissions
  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'accounts', label: 'All Accounts', icon: <PeopleIcon /> },
    { id: 'findings', label: 'Findings', icon: <GppBadIcon /> },
    { id: 'sync-history', label: 'Sync History', icon: <HistoryIcon /> },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: <NotificationsIcon />,
      badge: openAlertCount > 0,
      badgeCount: openAlertCount
    },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon />, show: isAdmin }
  ];

  // Filter tabs - show if no 'show' property or if 'show' is true
  const tabs = allTabs.filter(tab => tab.show === undefined || tab.show === true);

  const currentIndex = tabs.findIndex(tab => tab.id === currentView);
  const currentTab = tabs.find(tab => tab.id === currentView);

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileNavigate = (tabId) => {
    onNavigate(tabId);
    setMobileMenuOpen(false);
  };

  // Get role display info
  const getRoleInfo = () => {
    if (isAdmin) {
      return { label: 'Admin', color: '#dc2626', icon: <AdminPanelSettingsIcon sx={{ fontSize: 14 }} /> };
    }
    if (isReviewer) {
      return { label: 'Reviewer', color: '#d97706', icon: <RateReviewIcon sx={{ fontSize: 14 }} /> };
    }
    return { label: 'Viewer', color: '#6b7280', icon: <VisibilityIcon sx={{ fontSize: 14 }} /> };
  };

  const roleInfo = getRoleInfo();
  const visibleIntelligenceJobs = useMemo(() => intelligenceJobs.slice(0, 8), [intelligenceJobs]);

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Collapsed mini navbar
  if (collapsed) {
    return (
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: 40,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {/* Left: Expand button + Current page */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Expand header (Press H)">
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Mini logo */}
            <Box
              component="img"
              src="/assets/logo.png"
              alt="ATLAS"
              sx={{
                height: 24,
                width: 24,
                objectFit: 'contain',
                borderRadius: 0.5,
                bgcolor: 'rgba(255,255,255,0.95)'
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                ATLAS
              </Typography>
              <Tooltip title="View changelog">
                <Typography
                  variant="caption"
                  onClick={() => setChangelogOpen(true)}
                  sx={{
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: '0.65rem',
                    cursor: 'pointer',
                    '&:hover': { color: 'rgba(255,255,255,0.8)', textDecoration: 'underline' }
                  }}
                >
                  v{APP_VERSION}
                </Typography>
              </Tooltip>
            </Stack>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            {currentTab && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex' }}>
                  {React.cloneElement(currentTab.icon, { fontSize: 'small' })}
                </Box>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {currentTab.label}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Center: Quick nav tabs */}
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {tabs.map((tab) => (
              <Tooltip key={tab.id} title={tab.label}>
                <IconButton
                  size="small"
                  onClick={() => onNavigate(tab.id)}
                  sx={{
                    color: currentView === tab.id ? 'white' : 'rgba(255,255,255,0.5)',
                    bgcolor: currentView === tab.id ? 'rgba(255,255,255,0.15)' : 'transparent',
                    '&:hover': { 
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white'
                    }
                  }}
                >
                  {tab.badge ? (
                    <Badge
                      badgeContent={tab.badgeCount && tab.badgeCount > 0 ? tab.badgeCount : '!'}
                      color="error"
                      max={99}
                      sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}
                    >
                      {React.cloneElement(tab.icon, { fontSize: 'small' })}
                    </Badge>
                  ) : (
                    React.cloneElement(tab.icon, { fontSize: 'small' })
                  )}
                </IconButton>
              </Tooltip>
            ))}
          </Stack>

          {/* Right: User info */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={roleInfo.label}
              sx={{
                height: 20,
                bgcolor: roleInfo.color,
                color: 'white',
                fontSize: '0.65rem',
                display: { xs: 'none', sm: 'flex' }
              }}
            />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: { xs: 'none', sm: 'block' } }}>
              {user?.displayName?.split(' ')[0] || user?.email}
            </Typography>
            <Tooltip title="Account menu">
              <IconButton size="small" onClick={handleUserMenuOpen} sx={{ p: 0 }}>
                <Avatar
                  src={profilePicture}
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: roleInfo.color,
                    fontSize: '0.75rem'
                  }}
                >
                  {!profilePicture && getInitials(user?.displayName)}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Stack>

          {/* User Menu (same as expanded) */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { mt: 1, minWidth: 200, borderRadius: 2 }
            }}
          >
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.displayName || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            {authEnabled && (
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Sign out</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
        <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />
      </AppBar>
    );
  }

  // Full expanded navbar
  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #3b82f6 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: 72,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          {/* Logo & Title */}
          <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} alignItems="center" sx={{ flexShrink: 0 }}>
            {/* BGC Corporate Logo - Hide on small screens */}
            <Box
              component="img"
              src="/assets/BGC-Corp.png"
              alt="BGC Engineering"
              sx={{
                height: { xs: 32, md: 42 },
                width: 'auto',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
                opacity: 0.9,
                display: { xs: 'none', sm: 'block' }
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {/* ATLAS Logo */}
            <Box
              component="img"
              src="/assets/logo.png"
              alt="ATLAS"
              sx={{
                height: { xs: 36, md: 44 },
                width: { xs: 36, md: 44 },
                objectFit: 'contain',
                borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.95)',
                p: 0.5
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback if logo fails */}
            <Box
              sx={{
                display: 'none',
                width: { xs: 36, md: 48 },
                height: { xs: 36, md: 48 },
                borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.15)',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700,
                fontSize: '1.1rem'
              }}
            >
              A
            </Box>
            {/* Title - Hide on mobile when drawer is used */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Stack direction="row" spacing={0.75} alignItems="baseline">
                <Typography
                  variant="h5"
                  sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    letterSpacing: 1,
                    lineHeight: 1,
                    fontSize: { xs: '1.1rem', md: '1.5rem' }
                  }}
                >
                  ATLAS
                </Typography>
                <Tooltip title="View changelog">
                  <Typography
                    variant="caption"
                    onClick={() => setChangelogOpen(true)}
                    sx={{ 
                      color: 'rgba(255,255,255,0.5)', 
                      fontSize: '0.65rem',
                      display: { xs: 'none', md: 'inline' },
                      cursor: 'pointer',
                      '&:hover': { color: 'rgba(255,255,255,0.9)', textDecoration: 'underline' }
                    }}
                  >
                    v{APP_VERSION}
                  </Typography>
                </Tooltip>
              </Stack>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255,255,255,0.6)', 
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                  display: { xs: 'none', lg: 'block' }
                }}
              >
                Account Tracking & Lifecycle Admin
              </Typography>
            </Box>
          </Stack>

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                color: 'white',
                mx: 1,
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Navigation Tabs - Desktop Only */}
          {!isMobile && (
            <Tabs
              value={currentIndex >= 0 ? currentIndex : 0}
              onChange={(e, newValue) => onNavigate(tabs[newValue].id)}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flexGrow: 1,
                mx: { xs: 1, md: 3 },
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  minHeight: 72,
                  minWidth: 'auto',
                  px: { xs: 1.5, md: 2.5 },
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.95)',
                    bgcolor: 'rgba(255,255,255,0.08)'
                  },
                  '&.Mui-selected': {
                    color: 'white',
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  boxShadow: '0 0 10px rgba(255,255,255,0.5)'
                },
                '& .MuiTabs-scrollButtons': {
                  color: 'white',
                  '&.Mui-disabled': {
                    opacity: 0.3
                  }
                }
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  icon={
                    tab.badge ? (
                      <Badge
                        badgeContent={tab.badgeCount && tab.badgeCount > 0 ? tab.badgeCount : '!'}
                        color="error"
                        max={99}
                      >
                        {tab.icon}
                      </Badge>
                    ) : tab.icon
                  }
                  iconPosition="start"
                  label={tab.label}
                />
              ))}
            </Tabs>
          )}

          {/* Mobile: Show current page name */}
          {isMobile && currentTab && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, ml: 1 }}>
              <Box sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex' }}>
                {currentTab.icon}
              </Box>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                {currentTab.label}
              </Typography>
            </Stack>
          )}

          {/* Mobile Navigation Drawer */}
          <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={handleMobileMenuClose}
            PaperProps={{
              sx: {
                width: 280,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
              }
            }}
          >
            {/* Drawer Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  component="img"
                  src="/assets/logo.png"
                  alt="ATLAS"
                  sx={{
                    height: 36,
                    width: 36,
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    p: 0.25
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    ATLAS
                  </Typography>
                  <Typography
                    variant="caption"
                    onClick={() => { setChangelogOpen(true); setMobileMenuOpen(false); }}
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      '&:hover': { color: 'rgba(255,255,255,0.9)', textDecoration: 'underline' }
                    }}
                  >
                    v{APP_VERSION}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={handleMobileMenuClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Navigation Links */}
            <List sx={{ py: 2 }}>
              {tabs.map((tab) => (
                <ListItemButton
                  key={tab.id}
                  onClick={() => handleMobileNavigate(tab.id)}
                  selected={currentView === tab.id}
                  sx={{
                    py: 1.5,
                    px: 2.5,
                    color: currentView === tab.id ? 'white' : 'rgba(255,255,255,0.7)',
                    bgcolor: currentView === tab.id ? 'rgba(255,255,255,0.15)' : 'transparent',
                    borderLeft: currentView === tab.id ? '3px solid white' : '3px solid transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white'
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: 40 
                  }}>
                    {tab.badge ? (
                      <Badge
                        badgeContent={tab.badgeCount && tab.badgeCount > 0 ? tab.badgeCount : '!'}
                        color="error"
                        max={99}
                      >
                        {tab.icon}
                      </Badge>
                    ) : tab.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={tab.label}
                    primaryTypographyProps={{ fontWeight: currentView === tab.id ? 600 : 400 }}
                  />
                </ListItemButton>
              ))}
            </List>

            {/* User Info at Bottom */}
            {user && (
              <Box sx={{ 
                mt: 'auto', 
                borderTop: '1px solid rgba(255,255,255,0.1)',
                p: 2
              }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    src={profilePicture}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: roleInfo.color,
                      fontSize: '0.9rem'
                    }}
                  >
                    {!profilePicture && getInitials(user?.displayName)}
                  </Avatar>
                  <Box sx={{ overflow: 'hidden', flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }} noWrap>
                      {user.displayName || user.email}
                    </Typography>
                    <Chip
                      label={roleInfo.label}
                      size="small"
                      sx={{
                        height: 18,
                        mt: 0.5,
                        bgcolor: roleInfo.color,
                        color: 'white',
                        fontSize: '0.65rem'
                      }}
                    />
                  </Box>
                </Stack>
                {authEnabled && (
                  <ListItemButton
                    onClick={handleLogout}
                    sx={{
                      mt: 1.5,
                      py: 1,
                      borderRadius: 1,
                      color: '#fca5a5',
                      '&:hover': { bgcolor: 'rgba(239,68,68,0.2)' }
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Sign out" />
                  </ListItemButton>
                )}
              </Box>
            )}
          </Drawer>

          <Drawer
            anchor="right"
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            PaperProps={{
              sx: {
                width: { xs: '100%', sm: 380 }
              }
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Collect Intel Jobs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active and recent account intelligence collections
                </Typography>
              </Box>
              <IconButton onClick={() => setPanelOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <List sx={{ py: 0 }}>
              {visibleIntelligenceJobs.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No intelligence jobs yet.
                  </Typography>
                </Box>
              )}
              {visibleIntelligenceJobs.map((job) => {
                const steps = Object.values(job.progress?.steps || {});
                const completedSteps = steps.filter((step) => ['completed', 'skipped', 'partial', 'failed'].includes(step.status)).length;
                const progressValue = steps.length > 0 ? (completedSteps / steps.length) * 100 : 0;
                const currentStep = steps.find((step) => step.status === 'running') || steps[steps.length - 1];
                const statusColor = job.status === 'Completed'
                  ? 'success'
                  : job.status === 'Failed'
                    ? 'error'
                    : 'info';

                return (
                  <Box key={job.id} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                          {job.displayName || job.upn || 'Unknown account'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {job.upn || job.accountId}
                        </Typography>
                      </Box>
                      <Chip label={job.status} size="small" color={statusColor} variant={job.status === 'Running' ? 'filled' : 'outlined'} />
                    </Stack>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {currentStep?.message || currentStep?.label || 'Queued'}
                    </Typography>
                    {currentStep?.detail && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {currentStep.detail}
                      </Typography>
                    )}
                    <LinearProgress variant="determinate" value={progressValue} sx={{ mt: 1, height: 4, borderRadius: 999 }} />
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">
                        {completedSteps}/{steps.length || 0} steps complete
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.startedAt ? new Date(job.startedAt).toLocaleTimeString() : ''}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </List>
          </Drawer>

          {/* Right Section - Status & User */}
          <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center" sx={{ flexShrink: 0 }}>
            {/* Collapse Button - Hide on mobile */}
            <Tooltip title="Collapse header (Press H)">
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  display: { xs: 'none', lg: 'flex' },
                  '&:hover': { 
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'white'
                  }
                }}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            </Tooltip>

            {/* Sync status — click for stage progress / cancel */}
            <SyncStatusPopover
              onNavigateHistory={() => onNavigate('sync-history')}
              chipSx={{ display: { xs: 'none', lg: 'flex' } }}
            />

            <Chip
              label={activeJobCount > 0 ? `${activeJobCount} Collecting` : 'Intel Jobs'}
              size="small"
              onClick={() => setPanelOpen(true)}
              variant={activeJobCount > 0 ? 'filled' : 'outlined'}
              sx={{
                cursor: 'pointer',
                bgcolor: activeJobCount > 0 ? 'rgba(245, 158, 11, 0.18)' : 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                '&:hover': {
                  bgcolor: activeJobCount > 0 ? 'rgba(245, 158, 11, 0.28)' : 'rgba(255,255,255,0.16)',
                }
              }}
            />
            
            {/* User Section */}
            {user && (
              <Stack 
                direction="row" 
                spacing={{ xs: 1, md: 1.5 }}
                alignItems="center"
                sx={{ 
                  borderLeft: { xs: 'none', md: '1px solid rgba(255,255,255,0.15)' },
                  pl: { xs: 0, md: 2 },
                  ml: 0.5
                }}
              >
                {/* User Info - Hide on mobile */}
                <Box sx={{ textAlign: 'right', display: { xs: 'none', lg: 'block' } }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" sx={{ mb: 0.25 }}>
                    <Chip
                      icon={roleInfo.icon}
                      label={roleInfo.label}
                      size="small"
                      sx={{
                        height: 20,
                        bgcolor: roleInfo.color,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        '& .MuiChip-icon': {
                          color: 'white',
                          ml: '6px',
                          mr: '-2px'
                        },
                        '& .MuiChip-label': {
                          px: 0.75
                        }
                      }}
                    />
                  </Stack>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'white', 
                      lineHeight: 1.2,
                      fontSize: '0.875rem'
                    }}
                  >
                    {user.displayName || user.email}
                  </Typography>
                </Box>

                {/* Avatar with Profile Picture */}
                <Tooltip title="Account menu">
                  <IconButton
                    onClick={handleUserMenuOpen}
                    sx={{ 
                      p: 0.5,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    <Avatar 
                      src={profilePicture}
                      sx={{ 
                        width: 42, 
                        height: 42, 
                        bgcolor: roleInfo.color,
                        fontWeight: 600,
                        fontSize: '1rem',
                        border: '2px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                      }}
                    >
                      {!profilePicture && getInitials(user.displayName)}
                    </Avatar>
                  </IconButton>
                </Tooltip>

                {/* User Menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleUserMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      minWidth: 240,
                      borderRadius: 2,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  {/* User Header in Menu */}
                  <Box sx={{ px: 2.5, py: 2, bgcolor: 'grey.50' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar 
                        src={profilePicture}
                        sx={{ 
                          width: 48, 
                          height: 48, 
                          bgcolor: roleInfo.color,
                          fontSize: '1.1rem',
                          fontWeight: 600
                        }}
                      >
                        {!profilePicture && getInitials(user.displayName)}
                      </Avatar>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {user.displayName || 'User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  
                  <Divider />
                  
                  {/* Role Info */}
                  <MenuItem disabled sx={{ opacity: '1 !important' }}>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">Role:</Typography>
                        <Chip 
                          label={roleInfo.label} 
                          size="small" 
                          sx={{ 
                            bgcolor: roleInfo.color, 
                            color: 'white',
                            height: 22,
                            fontSize: '0.75rem'
                          }} 
                        />
                      </Stack>
                    </ListItemText>
                  </MenuItem>
                  
                  {/* User Types Access */}
                  {user.allowedUserTypes && user.allowedUserTypes.length > 0 && (
                    <MenuItem disabled sx={{ opacity: '1 !important' }}>
                      <ListItemIcon>
                        <PeopleIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography variant="body2" color="text.secondary">
                          Can view: <strong>{user.allowedUserTypes.join(', ')}</strong>
                        </Typography>
                      </ListItemText>
                    </MenuItem>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  {/* Sign Out */}
                  {authEnabled && (
                    <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText>Sign out</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </Stack>
            )}

            {/* Fallback when no user */}
            {!user && (
              <Box 
                sx={{ 
                  textAlign: 'right',
                  borderLeft: '1px solid rgba(255,255,255,0.2)',
                  pl: 2
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  Organization
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                  BGC Engineering Inc.
                </Typography>
              </Box>
            )}
          </Stack>
        </Toolbar>
      </Container>
      <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />
    </AppBar>
  );
}

export default NavbarMUI;
